import { MapViewerConfiguration } from "../../config/map-viewer-configuration";
import { DataTransporter } from "../data-transporter";
import { RootStoreSession } from "../root-store";
import { CreationProject, Project, ProjectFactory, UsageProject } from "./project";
import { CreationProjectDto, UsageProjectDto } from "./project-dto";
import _ from "lodash";
import { DataNotFound } from "../../globals/errors";

export class ProjectStore {
    mapViewerConfig: MapViewerConfiguration;
    rootStore: RootStoreSession;
    dataTransporter: DataTransporter;
    creationProjects: CreationProject[];
    usageProjects: UsageProject[];

    constructor(mapViewerConfig: MapViewerConfiguration, rootStore: RootStoreSession, dataTransporter: DataTransporter) {
        this.mapViewerConfig = mapViewerConfig;
        this.rootStore = rootStore;
        this.dataTransporter = dataTransporter;
        this.creationProjects = [];
        this.usageProjects = [];
    }

    async loadAll(): Promise<Project[]> {
        const creationProjects = await this.loadAllCreationProjects();
        const usageProjects = await this.loadAllUsageProjects();
        return ([] as Project[]).concat(creationProjects, usageProjects);
    }

    async loadAllCreationProjects(): Promise<CreationProject[]> {
        this.creationProjects = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-creation-projects`);
        const creationProjectDtos: CreationProjectDto[] = result.data;
        creationProjectDtos.forEach(p => this.creationProjects.push(ProjectFactory.makeProject(p, this.rootStore) as CreationProject));
        return this.creationProjects;
    }

    async loadAllUsageProjects(): Promise<UsageProject[]> {
        this.usageProjects = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-usage-projects`);
        const usageProjectDtos: UsageProjectDto[] = result.data;
        usageProjectDtos.forEach(p => this.usageProjects.push(ProjectFactory.makeProject(p, this.rootStore) as UsageProject));
        return this.usageProjects;
    }

    async loadCreationProject(id: number): Promise<CreationProject> {
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-creation-projects/${id}`);
        const projectDto: CreationProjectDto = result.data;
        const project = ProjectFactory.makeProject(projectDto, this.rootStore) as CreationProject;
        this.creationProjects.push(project);
        return project;
    }

    async loadUsageProject(id: number): Promise<UsageProject> {
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-usage-projects/${id}`);
        const projectDto: UsageProjectDto = result.data;
        const project = ProjectFactory.makeProject(projectDto, this.rootStore) as UsageProject;
        this.usageProjects.push(project);
        return project;
    }

    async addCreationProject(projectDto: CreationProjectDto): Promise<CreationProject> {
        const result = await this.dataTransporter.post(`${this.mapViewerConfig.api_base_url}/fff-creation-projects`, projectDto);
        const projectDtoResult: CreationProjectDto = result.data;
        const project = ProjectFactory.makeProject(projectDtoResult, this.rootStore) as CreationProject;
        this.creationProjects.push(project);
        return project;
    }

    async addUsageProject(projectDto: UsageProjectDto): Promise<UsageProject> {
        const result = await this.dataTransporter.post(`${this.mapViewerConfig.api_base_url}/fff-usage-projects`, projectDto);
        const projectDtoResult: UsageProjectDto = result.data;
        const project = ProjectFactory.makeProject(projectDtoResult, this.rootStore) as UsageProject;
        this.usageProjects.push(project);
        return project;
    }

    async updateCreationProject(projectDto: CreationProjectDto): Promise<CreationProject> {
        const project = this.creationProjects.find(p => p.id == projectDto.id);

        if (project !== undefined) {
            const result = await this.dataTransporter.put(`${this.mapViewerConfig.api_base_url}/fff-creation-projects/${projectDto.id}`, projectDto);
            project.projectDto = result.data;
            return project;
        }

        throw new DataNotFound("Creation Project not found");
    }

    async updateUsageProject(projectDto: UsageProjectDto): Promise<UsageProject> {
        const project = this.usageProjects.find(p => p.id == projectDto.id);

        if (project !== undefined) {
            const result = await this.dataTransporter.put(`${this.mapViewerConfig.api_base_url}/fff-usage-projects/${projectDto.id}`, projectDto);
            project.projectDto = result.data;
            return project;
        }

        throw new DataNotFound("Usage Project not found");
    }

    async patchUsageProject(projectId: number, compensationApproved: boolean) {
        const project = this.usageProjects.find(p => p.id === projectId);

        if (project !== undefined) {
            const result = await this.dataTransporter.patch(`${this.mapViewerConfig.api_base_url}/fff-usage-projects/${projectId}`, compensationApproved);
            project.projectDto = result.data;
            return project;
        }

        throw new DataNotFound("Usage Project not found");
    }

    async deleteCreationProject(id: number): Promise<CreationProject> {
        await this.dataTransporter.delete(`${this.mapViewerConfig.api_base_url}/fff-creation-projects/${id}`);
        const removedProjects: CreationProject[] = _.remove(this.creationProjects, p => p.id === id);
        return removedProjects[0];
    }

    async deleteUsageProject(id: number): Promise<UsageProject> {
        await this.dataTransporter.delete(`${this.mapViewerConfig.api_base_url}/fff-usage-projects/${id}`);
        const removedProjects: UsageProject[] = _.remove(this.usageProjects, p => p.id === id);
        return removedProjects[0];
    }
}
